/**
 * District Boundaries System
 * Provides geospatial boundary data and lookup functions for NWSISD districts
 */
import { supabase } from "@/integrations/supabase/client";
import { DistrictInfo } from '@/types/application';

// Define types for district boundary data
export interface DistrictBoundary {
  id: string;
  districtName: string;
  districtCode?: string;
  boundary: any; // GeoJSON representation
}

// Define the member districts with their basic information
export const NWSISD_MEMBER_DISTRICTS: DistrictInfo[] = [
  {
    id: 'anoka-hennepin',
    name: 'Anoka-Hennepin School District',
    number: 11,
    cities: ['Anoka', 'Andover', 'Champlin', 'Coon Rapids', 'Dayton', 'Ham Lake', 'Oak Grove', 'Ramsey'],
    zipCodes: ['55303', '55304', '55433', '55448', '55449', '55327', '55369']
  },
  {
    id: 'brooklyn-center',
    name: 'Brooklyn Center Community Schools',
    number: 286,
    cities: ['Brooklyn Center'],
    zipCodes: ['55429', '55430']
  },
  {
    id: 'buffalo-hanover-montrose',
    name: 'Buffalo-Hanover-Montrose Schools',
    number: 877,
    cities: ['Buffalo', 'Hanover', 'Montrose'],
    zipCodes: ['55313', '55341', '55363']
  },
  {
    id: 'isd728',
    name: 'ISD728 Elk River | Otsego | Rogers | Zimmerman',
    number: 728,
    cities: ['Elk River', 'Otsego', 'Rogers', 'Zimmerman'],
    zipCodes: ['55330', '55301', '55374', '55398']
  },
  {
    id: 'fridley',
    name: 'Fridley Public Schools',
    number: 14,
    cities: ['Fridley'],
    zipCodes: ['55432', '55421']
  },
  {
    id: 'osseo',
    name: 'Osseo Area Schools',
    number: 279,
    cities: ['Maple Grove', 'Osseo', 'Brooklyn Park', 'Plymouth', 'Corcoran', 'Dayton'],
    zipCodes: ['55311', '55369', '55443', '55444', '55445', '55442', '55446', '55340']
  },
  {
    id: 'rockford',
    name: 'Rockford Area Schools',
    number: 883,
    cities: ['Rockford'],
    zipCodes: ['55373']
  }
];

// Define boundary streets that divide districts
export const BOUNDARY_STREETS = {
  'Broadway Ave': [
    {
      fromNumber: 4000,
      toNumber: 5000,
      districtSide: 'west',
      district: 'brooklyn-center',
      oppositeDistrict: 'minneapolis'
    }
  ],
  'Bass Lake Road': [
    {
      fromNumber: 0,
      toNumber: 10000,
      districtSide: 'north',
      district: 'osseo',
      oppositeDistrict: 'minneapolis'
    }
  ],
  // Add more boundary streets as needed
};

// Global cached boundaries to prevent multiple fetches
let cachedDistrictBoundaries: DistrictBoundary[] | null = null;

/**
 * Fetches district boundary data from the database
 * Uses cached data if available
 */
export async function getDistrictBoundaries(): Promise<DistrictBoundary[]> {
  // Return cached data if available
  if (cachedDistrictBoundaries) {
    return cachedDistrictBoundaries;
  }

  try {
    const { data, error } = await supabase
      .from('district_boundaries')
      .select('id, district_name, district_code, boundary');

    if (error) {
      throw error;
    }

    // Transform data to match our interface
    const boundaries: DistrictBoundary[] = data.map(item => ({
      id: item.id,
      districtName: item.district_name,
      districtCode: item.district_code,
      boundary: item.boundary
    }));

    // Cache the boundaries
    cachedDistrictBoundaries = boundaries;
    return boundaries;
  } catch (error) {
    return [];
  }
}

/**
 * Checks if a district is one of the 7 official NWSISD member districts
 */
export function isNwsisdMemberDistrict(district: string | null): boolean {
  if (!district) return false;
  
  // Special handling for "potential" matches
  if (district.includes("Potential NWSISD District")) {
    return true;
  }
  
  return NWSISD_MEMBER_DISTRICTS.some(memberDistrict => 
    district.toLowerCase().includes(memberDistrict.name.toLowerCase())
  );
}

/**
 * Determines if a location is in a racially isolated area
 * Used for prioritization in the placement process
 */
export function isRaciallyIsolatedArea(city: string): boolean {
  return NWSISD_MEMBER_DISTRICTS.some(district => 
    district.cities.some(c => c.toLowerCase().includes(city.toLowerCase()))
  );
}

/**
 * Checks if a ZIP code is in a racially isolated area
 */
export function isRaciallyIsolatedZipCode(zipCode: string): boolean {
  return NWSISD_MEMBER_DISTRICTS.some(district => 
    district.zipCodes.includes(zipCode)
  );
}

/**
 * Gets the district for a city using the fallback city-to-district mapping
 */
export function getDistrictByCity(city: string): string | null {
  const normalizedCity = city.toLowerCase().trim();
  for (const district of NWSISD_MEMBER_DISTRICTS) {
    if (district.cities.some(c => c.toLowerCase() === normalizedCity)) {
      return district.id;
    }
  }
  return null;
}

/**
 * Function to find district by city (used by PostgreSQL functions)
 * This is a client-side version that matches the server-side function
 */
export function find_district_by_city(city: string): string | null {
  const district = getDistrictByCity(city);
  return district || 'Outside Known Districts';
}

// Helper function to determine district by ZIP code
export function getDistrictByZipCode(zipCode: string): string | null {
  for (const district of NWSISD_MEMBER_DISTRICTS) {
    if (district.zipCodes.includes(zipCode)) {
      return district.id;
    }
  }
  return null;
}

// Helper function to check if an address is on a boundary street
export function checkBoundaryStreet(street: string, houseNumber: number): {
  isBoundary: boolean;
  district?: string;
  oppositeDistrict?: string;
} {
  for (const [boundaryStreet, rules] of Object.entries(BOUNDARY_STREETS)) {
    if (street.toLowerCase().includes(boundaryStreet.toLowerCase())) {
      for (const rule of rules) {
        if (houseNumber >= rule.fromNumber && houseNumber <= rule.toNumber) {
          return {
            isBoundary: true,
            district: rule.district,
            oppositeDistrict: rule.oppositeDistrict
          };
        }
      }
    }
  }
  return { isBoundary: false };
}

// --- Diversity Priority Lists ---

// ZIP codes classified as high in racial diversity
export const ZIP_CODES_HIGH_DIVERSITY = [
  '55428', '55429', '55430', '55443', '55444', '55445'
];

// ZIP codes classified as low in racial diversity
export const ZIP_CODES_LOW_DIVERSITY = [
  '55005', '55011', '55092', '55109', '55110', '55112', '55113', '55115', '55126', '55127',
  '55301', '55303', '55304', '55311', '55313', '55316', '55327', '55328', '55330', '55340',
  '55341', '55353', '55362', '55363', '55369', '55373', '55374', '55376', '55377', '55379',
  '55390', '55421', '55432', '55433', '55434', '55439', '55448', '55449', '55452', '55454',
  '55569', '55572', '55577'
];

// Schools classified as low in racial diversity (by school ID or name)
export const SCHOOLS_LOW_DIVERSITY = [
  'Anoka Middle School for the Arts',
  'Anoka High School',
  'Blaine High School Center for Engineering, Mathematics and Science',
  'Coon Rapids High School',
  'Montrose Elementary',
  'Osseo Senior High Medical Careers Program',
  'Rockford Elementary Arts Magnet School',
  'Rockford Middle School – Center for Environmental Studies',
  'Rockford High School – An IB World School',
  'Rogers Elementary STEM Program',
  'Salk Middle School Pre-Engineering Magnet Program',
  'Tatanka Elementary Science, Technology, Engineering, and Math',
  'Weaver Lake Elementary: A Science, Math and Technology School',
  'University Avenue Elementary: Aerospace, Children\'s Engineering, and Science'
];

// Schools classified as high in racial diversity (by school ID or name)
export const SCHOOLS_HIGH_DIVERSITY = [
  'Birch Grove Elementary School for the Arts',
  'Brooklyn Center Middle/High School STEAM',
  'Brooklyn Middle STEM Program',
  'Brooklyn Center Elementary STEAM',
  'Evergreen Elementary',
  'Fridley PreK4 – An IB World School',
  'Fridley Middle School – An IB World School',
  'Fridley High School – An IB World School',
  'Hayes Elementary – An IB World School',
  'R.L. Stevenson Elementary – An IB World School',
  'Zanewood Elementary'
];

/**
 * Determines if an applicant receives diversity priority for a given school.
 * Returns true if:
 *   - Applicant zip is high diversity and school is low diversity, OR
 *   - Applicant zip is low diversity and school is high diversity
 */
export function hasDiversityPriority(applicantZip: string, schoolName: string): boolean {
  const zipHigh = ZIP_CODES_HIGH_DIVERSITY.includes(applicantZip);
  const zipLow = ZIP_CODES_LOW_DIVERSITY.includes(applicantZip);
  const schoolHigh = SCHOOLS_HIGH_DIVERSITY.includes(schoolName);
  const schoolLow = SCHOOLS_LOW_DIVERSITY.includes(schoolName);

  return (zipHigh && schoolLow) || (zipLow && schoolHigh);
}

export const districtNamesList = NWSISD_MEMBER_DISTRICTS.map(d => d.name);
